'use client';

import type {
  ScheduledReportFrequency,
  ScheduledReportRule,
} from '@terra/shared-contracts-applications';
import { PlayIcon, StopIcon } from '@terra/ui-icons';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useMemo, useState, type ReactNode } from 'react';
import { Badge } from '../../../../components/badge';
import { Spinner } from '../../../../components/spinner';
import { BodyDefault, MetaDefault } from '../../../../components/typography';
import { SettingsBooleanControl } from '../../SettingsBooleanControl';
import { SettingsCard } from '../../SettingsCard';
import { SettingsRow } from '../../SettingsRow';
import { RequestsPerMinuteSlider } from '../RequestsPerMinuteSlider';
import { ScheduledReportFrequencyDialog } from '../ScheduledReportFrequencyDialog';
import { ScheduledReportRuleBlock } from '../ScheduledReportRuleBlock';
import {
  SCHEDULED_REPORT_FREQUENCIES,
  changeRuleFrequency,
  formatNextScheduledReportLabel,
  formatReportFrequencySummary,
  getSoonestScheduledReportOccurrence,
} from '../scheduled-report-rule.utils';
import {
  DynamicApplicationScanLastRunCard,
  type DynamicApplicationScanRunSummary,
} from './DynamicApplicationScanLastRunCard';

export type DynamicApplicationScanSettingsCardProps = {
  vulnerabilityEnabled: boolean;
  directoryEnabled: boolean;
  requestsPerMinute: number | null | undefined;
  isScheduleOpen: boolean;
  scheduleRule: ScheduledReportRule | null;
  nextRunAt?: Date | string | null;
  activeRunStatus?: string | null;
  lastRun?: DynamicApplicationScanRunSummary | null;
  hasUrl?: boolean;
  hasScanTargets?: boolean;
  banner?: ReactNode;
  disabled?: boolean;
  isReadOnly?: boolean;
  isRunNowBlocked?: boolean;
  isRequestsPerMinuteSaving?: boolean;
  isTriggering?: boolean;
  isStopping?: boolean;
  onVulnerabilityEnabledChange: (checked: boolean) => void;
  onDirectoryEnabledChange: (checked: boolean) => void;
  onRequestsPerMinuteCommit: (next: number | null) => Promise<boolean>;
  onScheduleEnabledChange: (checked: boolean) => void;
  onScheduleRuleChange: (rule: ScheduledReportRule) => void;
  onRunNow: () => void;
  onStop: () => void;
};

const ACTIVE_RUN_STATUSES: ReadonlyArray<string> = ['RUNNING', 'QUEUED'];

export function DynamicApplicationScanSettingsCard({
  vulnerabilityEnabled,
  directoryEnabled,
  requestsPerMinute,
  isScheduleOpen,
  scheduleRule,
  nextRunAt,
  activeRunStatus,
  lastRun,
  hasUrl,
  hasScanTargets,
  banner,
  disabled,
  isReadOnly,
  isRunNowBlocked,
  isRequestsPerMinuteSaving,
  isTriggering,
  isStopping,
  onVulnerabilityEnabledChange,
  onDirectoryEnabledChange,
  onRequestsPerMinuteCommit,
  onScheduleEnabledChange,
  onScheduleRuleChange,
  onRunNow,
  onStop,
}: DynamicApplicationScanSettingsCardProps) {
  const t = useTranslations('designSystem.automation.dynamicApplicationScan');
  const tReports = useTranslations('designSystem.automation.scheduledReports');
  const tCommon = useTranslations('designSystem.common');
  const [isFrequencyDialogOpen, setIsFrequencyDialogOpen] = useState(false);
  const controlsDisabled = Boolean(disabled);
  const isRunActive = activeRunStatus
    ? ACTIVE_RUN_STATUSES.includes(activeRunStatus)
    : false;
  const isScanActive = Boolean(isTriggering || isStopping) || isRunActive;
  const runNowDisabled =
    controlsDisabled ||
    Boolean(isRunNowBlocked) ||
    !hasUrl ||
    !hasScanTargets ||
    !(vulnerabilityEnabled || directoryEnabled) ||
    isScanActive;
  const frequencySummary =
    (scheduleRule
      ? formatReportFrequencySummary({
          rules: [scheduleRule],
          getFrequencyLabel: (frequency) =>
            tReports(`frequencies.${frequency}`),
        })
      : null) ?? tReports('frequencyNone');
  const nextOccurrence = useMemo(
    () =>
      scheduleRule ? getSoonestScheduledReportOccurrence([scheduleRule]) : null,
    [scheduleRule],
  );
  const nextRunLabel =
    formatNextScheduledReportLabel({
      nextScheduledReportAt: nextOccurrence?.runAt ?? nextRunAt,
      timezone: nextOccurrence?.timezone ?? scheduleRule?.timezone,
    }) ?? tReports('notScheduled');
  const scheduleFrequency = scheduleRule?.frequency;
  const frequencyDialogInitial = useMemo(
    () => (scheduleFrequency ? [scheduleFrequency] : undefined),
    [scheduleFrequency],
  );

  const handleScheduleEnabledChange = (checked: boolean) => {
    if (!checked) {
      setIsFrequencyDialogOpen(false);
    }

    onScheduleEnabledChange(checked);
  };

  const handleFrequencyChange = (frequencies: ScheduledReportFrequency[]) => {
    const nextFrequency = frequencies[0];

    if (!nextFrequency || !scheduleRule) {
      return;
    }

    onScheduleRuleChange(
      changeRuleFrequency({ rule: scheduleRule, frequency: nextFrequency }),
    );
  };

  const renderSwitch = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <SettingsBooleanControl
      checked={checked}
      label={checked ? tCommon('yes') : tCommon('no')}
      disabled={controlsDisabled}
      onCheckedChange={(details) => {
        if (typeof details.checked !== 'boolean') {
          return;
        }

        onChange(details.checked);
      }}
    />
  );

  return (
    <VStack alignItems="stretch" gap={3}>
      {banner}
      <SettingsCard>
        <VStack alignItems="stretch" gap={4} w="full">
          <SettingsRow
            label={t('vulnerabilityEnabled.label')}
            tooltip={t('vulnerabilityEnabled.tooltip')}
            control={renderSwitch({
              checked: vulnerabilityEnabled,
              onChange: onVulnerabilityEnabledChange,
            })}
          />

          <SettingsRow
            label={t('directoryEnabled.label')}
            tooltip={t('directoryEnabled.tooltip')}
            control={renderSwitch({
              checked: directoryEnabled,
              onChange: onDirectoryEnabledChange,
            })}
          />

          <RequestsPerMinuteSlider
            label={t('requestsPerMinute.label')}
            tooltip={t('requestsPerMinute.tooltip')}
            value={requestsPerMinute}
            disabled={controlsDisabled || Boolean(isRequestsPerMinuteSaving)}
            onCommit={onRequestsPerMinuteCommit}
          />

          <Box h="1px" bg="border.default" />

          <SettingsRow
            label={t('enabled.label')}
            tooltip={t('enabled.tooltip')}
            control={renderSwitch({
              checked: isScheduleOpen,
              onChange: handleScheduleEnabledChange,
            })}
          />

          {isScheduleOpen ? (
            <>
              <SettingsRow
                label={
                  <BodyDefault color="fg.neutral.default">
                    {t('scanFrequency')}
                  </BodyDefault>
                }
                value={frequencySummary}
                action={{
                  label: tCommon('edit'),
                  onClick: () => setIsFrequencyDialogOpen(true),
                  disabled: controlsDisabled || !scheduleRule,
                }}
                isReadOnly={isReadOnly}
              />

              {scheduleRule ? (
                <Box
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor="border.default"
                  borderRadius="md"
                  m="3px"
                  p={3}
                >
                  <ScheduledReportRuleBlock
                    rule={scheduleRule}
                    disabled={isReadOnly}
                    canRemove={false}
                    onChange={onScheduleRuleChange}
                  />
                </Box>
              ) : null}

              <MetaDefault color="fg.neutral.muted">
                {t('nextRun', { date: nextRunLabel })}
              </MetaDefault>
            </>
          ) : null}

          <Box h="1px" bg="border.default" />

          <SettingsRow
            label={t('runNow.label')}
            control={
              isScanActive ? (
                <Badge
                  size="sm"
                  bg={token('colors.bg.accent.light')}
                  variant="fill"
                  color={token('colors.fg.accent.muted')}
                >
                  <HStack gap={1} alignItems="center">
                    <Spinner size="sm" borderColor="bg.accent.default" />
                    {t('runNow.running')}
                  </HStack>
                </Badge>
              ) : undefined
            }
            action={
              isScanActive
                ? {
                    label: t('stop'),
                    icon: <StopIcon size={16} />,
                    onClick: onStop,
                    disabled: controlsDisabled || Boolean(isStopping),
                  }
                : {
                    label: t('runNow.action'),
                    icon: <PlayIcon size={16} />,
                    onClick: onRunNow,
                    disabled: runNowDisabled,
                  }
            }
            isReadOnly={isReadOnly}
          />
        </VStack>
      </SettingsCard>

      {lastRun ? <DynamicApplicationScanLastRunCard lastRun={lastRun} /> : null}

      {hasUrl === false ? <MetaDefault>{t('missingUrl')}</MetaDefault> : null}

      {hasUrl && hasScanTargets === false ? (
        <MetaDefault>{t('missingScanTargets')}</MetaDefault>
      ) : null}

      <ScheduledReportFrequencyDialog
        open={isFrequencyDialogOpen}
        disabled={controlsDisabled}
        availableFrequencies={[...SCHEDULED_REPORT_FREQUENCIES]}
        selectionMode="single"
        initialFrequencies={frequencyDialogInitial}
        submitText={tCommon('saveChanges')}
        onOpenChange={setIsFrequencyDialogOpen}
        onAdd={handleFrequencyChange}
      />
    </VStack>
  );
}
